import { useCallback, useEffect, useRef, useState } from 'react';
import { comprobanteService } from '../services/comprobanteService';
import { notificacionBalanceService } from '../services/notificacionBalanceService';
import type {
  EntidadDinero, MedioPago, RespaldoMovimiento, SeleccionComprobante,
} from '../types/comprobante';
import { cuentaParaRegularizacion } from '../utils/comprobantePlazo';
import { useExigenciaConRevalidacion } from './useExigenciaComprobantes';

export const MENSAJE_COMPROBANTE_FALTANTE =
  'Adjunta el comprobante (foto de la boleta, recibo o transferencia, o su PDF) o elige subirlo después.';
export const MENSAJE_COMPROBANTE_EN_EL_ACTO =
  'Adjunta el comprobante (foto de la boleta, recibo o transferencia, o su PDF).';
export const MENSAJE_MEDIO_PAGO = 'Indica si el pago fue con débito, por transferencia o en efectivo.';
export const MENSAJE_FOLIO = 'Ingresa el folio del recibo de talonario.';
/** Contiene "necesita su comprobante": `mensajeErrorDeNegocio` lo muestra en el formulario, no como falla. */
export const MENSAJE_EXIGENCIA_RECIEN_VIGENTE =
  'Este movimiento necesita su comprobante y su medio de pago: revísalos en el formulario y vuelve a guardar.';
const MENSAJE_SIN_PLAZO = 'Este movimiento no puede quedar pendiente: adjunta el comprobante ahora.';
const FOLIO_RE = /^[1-9]\d{0,8}$/;

/** desde_vigencia: lo normal. siempre: adjuntar o reemplazar. nunca: donaciones y aportes en especie. */
export type Obligatoriedad = 'desde_vigencia' | 'siempre' | 'nunca';

/**
 * Hacia dónde va el dinero, desde la entidad que registra. 'ingreso': la entidad recibe y su comprobante
 * en efectivo es siempre un recibo de su talonario. 'egreso': la entidad paga; el comprobante es la boleta
 * o factura que le entregaron o, si no hubo, un recibo de talonario firmado por quien recibió.
 */
export type SentidoDinero = 'ingreso' | 'egreso';

/** Con ingreso en efectivo el recibo de talonario es la única opción; con egreso, el usuario lo elige. */
export function esReciboTalonarioEfectivo(sentido: SentidoDinero, sinBoletaElegido: boolean): boolean {
  return sentido === 'ingreso' || sinBoletaElegido;
}

interface OpcionesBase {
  readonly entidad: EntidadDinero | null;
  /** Fecha del movimiento (YYYY-MM-DD): decide si su mes ya está rendido. */
  readonly fechaMovimiento: string | null;
  readonly permiteSubirDespues: boolean;
  readonly obligatoriedad?: Obligatoriedad;
  /** Mensaje cuando falta el comprobante y no hay "subir después". */
  readonly mensajeFaltante?: string;
  /** Al editar: se excluye de la advertencia de folio repetido. */
  readonly movimientoId?: string;
  readonly medioPagoInicial?: MedioPago | null;
  readonly folioInicial?: number | null;
}

/** El sentido del dinero es obligatorio en cuanto el formulario pide medio de pago (y con él, el folio). */
export type OpcionesComprobanteFormulario = OpcionesBase & (
  | { readonly pideMedioPago: false; readonly sentidoDinero?: SentidoDinero }
  | { readonly pideMedioPago: boolean; readonly sentidoDinero: SentidoDinero }
);

export interface ComprobanteFormulario {
  readonly entidad: EntidadDinero | null;
  readonly seleccion: SeleccionComprobante;
  readonly cambiarSeleccion: (seleccion: SeleccionComprobante) => void;
  readonly medioPago: MedioPago | '';
  readonly cambiarMedioPago: (medio: MedioPago | '') => void;
  readonly sentidoDinero: SentidoDinero;
  /** true cuando, en efectivo, el comprobante es un recibo de talonario propio y por tanto lleva folio. */
  readonly esReciboTalonario: boolean;
  /** Solo tiene efecto en egresos: ahí el usuario indica si hubo boleta o factura (false) o no (true). */
  readonly cambiarEsReciboTalonario: (valor: boolean) => void;
  readonly folio: string;
  readonly cambiarFolio: (folio: string) => void;
  /** false en marcha blanca y al editar un registro sin folio: el campo se rotula "(opcional)". */
  readonly folioObligatorio: boolean;
  readonly folioRepetido: boolean;
  readonly verificarFolio: () => void;
  readonly pideMedioPago: boolean;
  /** true cuando el comprobante es obligatorio en este formulario, hoy. Si no, el bloque dice "(opcional)". */
  readonly exigible: boolean;
  /**
   * true solo cuando la base confirmó que la exigencia general aún no rige. Con la respuesta pendiente
   * o con error es false: el aviso de marcha blanca no se muestra sin saberlo. No depende de
   * `exigible`, porque adjuntar o reemplazar desde la lista de pendientes es obligatorio ("siempre")
   * y aun así ocurre en marcha blanca.
   */
  readonly enMarchaBlanca: boolean;
  readonly puedeSubirDespues: boolean;
  readonly subiendo: boolean;
  readonly hayCambios: boolean;
  readonly validar: () => string | null;
  readonly resolver: () => Promise<RespaldoMovimiento>;
  readonly reiniciar: () => void;
}

function revocarVistasPrevias(seleccion: SeleccionComprobante): void {
  if (seleccion.tipo === 'imagenes') seleccion.paginas.forEach((p) => { URL.revokeObjectURL(p.vistaPrevia); });
}

/**
 * Un formulario abierto en marcha blanca puede quedar desfasado si la exigencia empieza a regir con la
 * pantalla ya abierta. Con la respuesta fresca, ¿falta algo que el formulario no pedía?
 */
export function faltaAlRegirLaExigencia(estado: {
  readonly obligatoriedad: Obligatoriedad;
  readonly seleccionTipo: SeleccionComprobante['tipo'];
  readonly pideMedioPago: boolean;
  readonly medioPago: MedioPago | '';
}): boolean {
  if (estado.obligatoriedad !== 'desde_vigencia') return false;
  return estado.seleccionTipo === 'ninguno' || (estado.pideMedioPago && estado.medioPago === '');
}

export const MENSAJE_SIN_SESION_PARA_GUARDAR =
  'Tu sesión ya no está disponible: vuelve a iniciar sesión para guardar.';

/**
 * Último control antes de escribir. Corre para todo estado distinto de "ya rige" (aún no, o todavía
 * sin saberse porque la consulta inicial sigue en vuelo o falló), salvo en formularios que nunca la
 * exigen. Pregunta de nuevo y, sin actor autenticado, bloquea en vez de dar el silencio por permiso.
 * Un fallo de la consulta cuenta como "rige" (cierra hacia exigir).
 */
export async function verificarExigenciaAntesDeGuardar(estado: {
  readonly exigenciaVigente: boolean | null;
  readonly revalidar: () => Promise<boolean | null>;
  readonly obligatoriedad: Obligatoriedad;
  readonly seleccionTipo: SeleccionComprobante['tipo'];
  readonly pideMedioPago: boolean;
  readonly medioPago: MedioPago | '';
  readonly esReciboTalonario: boolean;
  readonly folio: string;
  readonly esEdicion: boolean;
  readonly folioInicial: number | null;
}): Promise<void> {
  if (estado.obligatoriedad !== 'desde_vigencia' || estado.exigenciaVigente === true) return;
  const rigeAhora = await estado.revalidar();
  if (rigeAhora === null) throw new Error(MENSAJE_SIN_SESION_PARA_GUARDAR);
  if (!rigeAhora) return;
  if (faltaAlRegirLaExigencia(estado)) throw new Error(MENSAJE_EXIGENCIA_RECIEN_VIGENTE);
  // El folio pasa a ser obligatorio con la vigencia, con la misma excepción de editar un registro sin folio.
  if (!estado.pideMedioPago) return;
  const folioObligatorio = folioEsObligatorio(true, estado.esEdicion, estado.folioInicial);
  const mensajeFolio = validarMedioPagoYFolio(true, estado.medioPago, estado.esReciboTalonario, estado.folio, folioObligatorio);
  if (mensajeFolio) throw new Error(mensajeFolio);
}

/**
 * El folio sigue la marcha blanca del medio de pago: solo es obligatorio cuando la exigencia rige. Al editar
 * un registro que no tenía folio nunca lo es, para poder corregir datos sin inventar uno.
 */
export function folioEsObligatorio(
  medioExigible: boolean, esEdicion: boolean, folioInicial: number | null,
): boolean {
  return medioExigible && !(esEdicion && folioInicial === null);
}

/** Con folio opcional, vacío es válido; escrito, siempre se valida su formato. */
export function validarMedioPagoYFolio(
  medioExigible: boolean, medioPago: MedioPago | '', esReciboTalonario: boolean, folio: string,
  folioObligatorio: boolean = medioExigible,
): string | null {
  if (medioExigible && medioPago === '') return MENSAJE_MEDIO_PAGO;
  if (medioPago !== 'efectivo' || !esReciboTalonario) return null;
  if (folio === '') return folioObligatorio ? MENSAJE_FOLIO : null;
  return FOLIO_RE.test(folio) ? null : MENSAJE_FOLIO;
}

/** El folio solo cuenta en efectivo con recibo de talonario y bien escrito. */
export function folioNumerico(medioPago: MedioPago | '', esReciboTalonario: boolean, folio: string): number | null {
  return medioPago === 'efectivo' && esReciboTalonario && FOLIO_RE.test(folio) ? Number(folio) : null;
}

/**
 * ¿Se puede ofrecer "subir después"? Solo si la entidad no está en regularización ni rindió ya el mes del
 * movimiento. Sin poder verificarlo, no se ofrece: pedir el comprobante es lo conservador.
 */
function usePlazoDisponible(
  permiteSubirDespues: boolean, entidad: EntidadDinero | null, fechaMovimiento: string | null,
): boolean {
  // El plazo se verificó para una entidad y un mes concretos; con otra clave deja de valer.
  const [verificado, setVerificado] = useState<{ readonly clave: string; readonly disponible: boolean } | null>(null);
  const mesMovimiento = fechaMovimiento ? fechaMovimiento.slice(0, 7) : null;
  // El efecto depende del tipo y el id, no del objeto: los formularios arman la entidad en cada render
  // y un objeto nuevo repetiría la consulta sin fin.
  const entidadTipo = entidad?.tipo ?? null;
  const entidadId = entidad?.id ?? null;
  const clave = entidadTipo && entidadId ? `${entidadTipo}:${entidadId}:${mesMovimiento ?? ''}` : null;

  useEffect(() => {
    if (!permiteSubirDespues || !entidadTipo || !entidadId || !clave) return;
    const entidadConsultada: EntidadDinero = { tipo: entidadTipo, id: entidadId, nombre: '' };
    let vigente = true;
    const [anio, mes] = (mesMovimiento ?? '').split('-').map(Number);
    const consultarMes = entidadTipo !== 'provincia' && anio && mes
      ? notificacionBalanceService.getByEntidadYPeriodo(entidadId, mes, anio)
      : Promise.resolve(null);
    Promise.all([comprobanteService.getFaltantes(entidadConsultada), consultarMes])
      .then(([faltantes, notificacion]) => {
        if (!vigente) return;
        const ahora = new Date();
        const enRegularizacion = faltantes.some((f) => cuentaParaRegularizacion(f.pendiente_desde, ahora));
        const mesRendido = notificacion?.estado === 'enviado' || notificacion?.estado === 'recibido';
        setVerificado({ clave, disponible: !enRegularizacion && !mesRendido });
      })
      .catch(() => { if (vigente) setVerificado({ clave, disponible: false }); });
    return () => { vigente = false; };
  }, [permiteSubirDespues, entidadTipo, entidadId, mesMovimiento, clave]);

  return verificado !== null && verificado.clave === clave && verificado.disponible;
}

export function useComprobanteFormulario(opciones: OpcionesComprobanteFormulario): ComprobanteFormulario {
  const {
    entidad, fechaMovimiento, permiteSubirDespues, pideMedioPago,
    obligatoriedad = 'desde_vigencia', mensajeFaltante, movimientoId,
    medioPagoInicial = null, folioInicial = null,
  } = opciones;
  // Sin medio de pago no hay folio y el sentido no se usa.
  const sentidoDinero: SentidoDinero = opciones.sentidoDinero ?? 'egreso';

  const [seleccion, setSeleccion] = useState<SeleccionComprobante>({ tipo: 'ninguno' });
  const [medioPago, setMedioPago] = useState<MedioPago | ''>(medioPagoInicial ?? '');
  // Egreso: parte en "te entregaron boleta" salvo que el registro editado ya tenga folio.
  const [sinBoletaElegido, setSinBoletaElegido] = useState(folioInicial !== null);
  const esReciboTalonario = esReciboTalonarioEfectivo(sentidoDinero, sinBoletaElegido);
  const [folio, setFolio] = useState(folioInicial === null ? '' : String(folioInicial));
  const [folioRepetido, setFolioRepetido] = useState(false);
  const { rige: exigenciaVigente, revalidar: revalidarExigencia } = useExigenciaConRevalidacion();
  const [subiendo, setSubiendo] = useState(false);
  const subidoRef = useRef<string | null>(null);
  const seleccionRef = useRef(seleccion);
  useEffect(() => { seleccionRef.current = seleccion; }, [seleccion]);

  const plazoDisponible = usePlazoDisponible(permiteSubirDespues, entidad, fechaMovimiento);

  // Libera las vistas previas al desmontar el formulario.
  useEffect(() => () => { revocarVistasPrevias(seleccionRef.current); }, []);

  const rige = exigenciaVigente === true;
  const enMarchaBlanca = exigenciaVigente === false;
  const exigible = obligatoriedad === 'siempre' || (obligatoriedad === 'desde_vigencia' && rige);
  // Antes de la entrada en vigor el bloque es opcional, se rotula "(opcional)" y no ofrece "subir
  // después": no hay plazos para quien no adjunta (H17). El formulario no dice desde cuándo será
  // obligatorio (H18).
  const puedeSubirDespues = permiteSubirDespues && exigible && plazoDisponible;
  const medioExigible = pideMedioPago && obligatoriedad === 'desde_vigencia' && rige;
  const folioObligatorio = folioEsObligatorio(medioExigible, movimientoId !== undefined, folioInicial);

  const cambiarSeleccion = useCallback((nueva: SeleccionComprobante) => {
    subidoRef.current = null;
    setSeleccion(nueva);
  }, []);

  const cambiarMedioPago = useCallback((medio: MedioPago | '') => {
    setMedioPago(medio);
    if (medio !== 'efectivo') { setSinBoletaElegido(false); setFolio(''); setFolioRepetido(false); }
  }, []);

  const cambiarEsReciboTalonario = useCallback((valor: boolean) => {
    setSinBoletaElegido(valor);
    if (!valor) { setFolio(''); setFolioRepetido(false); }
  }, []);

  const cambiarFolio = useCallback((valor: string) => {
    setFolio(valor);
    setFolioRepetido(false);
  }, []);

  const verificarFolio = useCallback(() => {
    if (!entidad || !FOLIO_RE.test(folio)) return;
    comprobanteService.hayFolioRepetido(entidad, Number(folio), movimientoId)
      .then(setFolioRepetido)
      // Es solo una advertencia: si no se puede consultar, no se muestra.
      .catch(() => { setFolioRepetido(false); });
  }, [entidad, folio, movimientoId]);

  const validar = useCallback((): string | null => {
    if (exigible && seleccion.tipo === 'ninguno') {
      return mensajeFaltante ?? (permiteSubirDespues ? MENSAJE_COMPROBANTE_FALTANTE : MENSAJE_COMPROBANTE_EN_EL_ACTO);
    }
    if (seleccion.tipo === 'despues' && !puedeSubirDespues) return MENSAJE_SIN_PLAZO;
    return validarMedioPagoYFolio(medioExigible, medioPago, esReciboTalonario, folio, folioObligatorio);
  }, [exigible, seleccion.tipo, mensajeFaltante, permiteSubirDespues, puedeSubirDespues, medioExigible, medioPago, esReciboTalonario, folio, folioObligatorio]);

  const resolver = useCallback(async (): Promise<RespaldoMovimiento> => {
    await verificarExigenciaAntesDeGuardar({
      exigenciaVigente, revalidar: revalidarExigencia, obligatoriedad,
      seleccionTipo: seleccion.tipo, pideMedioPago, medioPago,
      esReciboTalonario, folio, esEdicion: movimientoId !== undefined, folioInicial,
    });
    const base = {
      medio_pago: pideMedioPago && medioPago !== '' ? medioPago : null,
      folio_recibo: folioNumerico(medioPago, esReciboTalonario, folio),
    };
    if (seleccion.tipo === 'ninguno') return { ...base, comprobante_id: null, comprobante_pendiente_desde: null };
    // La base reemplaza esta marca por su propio now(); el cliente solo declara la intención.
    if (seleccion.tipo === 'despues') return { ...base, comprobante_id: null, comprobante_pendiente_desde: new Date().toISOString() };
    if (seleccion.tipo === 'existente') return { ...base, comprobante_id: seleccion.comprobante.id, comprobante_pendiente_desde: null };
    if (!entidad) throw new Error('Falta la entidad dueña del comprobante');

    // Subido en un intento anterior (falló el guardado del movimiento): no se sube de nuevo.
    if (subidoRef.current) return { ...base, comprobante_id: subidoRef.current, comprobante_pendiente_desde: null };
    setSubiendo(true);
    try {
      const id = await comprobanteService.subir(
        entidad,
        seleccion.tipo === 'pdf'
          ? { tipo: 'pdf', archivo: seleccion.archivo }
          : { tipo: 'imagen', paginas: seleccion.paginas.map((p) => p.blob) },
      );
      subidoRef.current = id;
      return { ...base, comprobante_id: id, comprobante_pendiente_desde: null };
    } finally {
      setSubiendo(false);
    }
  }, [exigenciaVigente, obligatoriedad, revalidarExigencia, pideMedioPago, medioPago, esReciboTalonario, folio, folioInicial, movimientoId, seleccion, entidad]);

  const reiniciar = useCallback(() => {
    revocarVistasPrevias(seleccionRef.current);
    subidoRef.current = null;
    setSeleccion({ tipo: 'ninguno' });
    setMedioPago(medioPagoInicial ?? '');
    setSinBoletaElegido(folioInicial !== null);
    setFolio(folioInicial === null ? '' : String(folioInicial));
    setFolioRepetido(false);
  }, [medioPagoInicial, folioInicial]);

  const hayCambios = seleccion.tipo !== 'ninguno'
    || medioPago !== (medioPagoInicial ?? '')
    || folio !== (folioInicial === null ? '' : String(folioInicial));

  return {
    entidad, seleccion, cambiarSeleccion, medioPago, cambiarMedioPago, sentidoDinero, esReciboTalonario,
    cambiarEsReciboTalonario, folio, cambiarFolio, folioObligatorio, folioRepetido, verificarFolio, pideMedioPago,
    exigible, enMarchaBlanca, puedeSubirDespues, subiendo, hayCambios, validar, resolver, reiniciar,
  };
}
